/**
 * Feature access configuration mapping features to their minimum required subscription tier.
 *
 * Tier hierarchy: free < plus < premium
 * "plus"    → available to Plus, Premium, Platinum, Coach
 * "premium" → available to Premium, Platinum, Coach only
 */

export type RequiredTier = "plus" | "premium";

export interface FeatureGate {
  requiredTier: RequiredTier;
  title: string;
  description: string;
  upgradeButtonLabel: string;
}

/**
 * Central registry of gated features.
 * Keys are human-readable identifiers used in code to look up the gate.
 */
export const FEATURE_GATES: Record<string, FeatureGate> = {
  // ─── LOGGING & TRAINING DIARY ────────────────────────────
  peakScore: {
    requiredTier: "plus",
    title: "Plus Feature",
    description:
      "Peak Score is available on the Plus plan. Upgrade to unlock your daily readiness score and track your performance over time.",
    upgradeButtonLabel: "Upgrade to Plus",
  },
  peakReadiness: {
    requiredTier: "plus",
    title: "Plus Feature",
    description:
      "Peak Readiness charts are available on the Plus plan. Upgrade to track your readiness trends and gain deeper performance insights.",
    upgradeButtonLabel: "Upgrade to Plus",
  },
  // Alias used by native apps for Peak Readiness / Load Balance V2 pages.
  peakReadinessV2: {
    requiredTier: "plus",
    title: "Plus Feature",
    description:
      "Peak Readiness and Load Balance are available on the Plus plan. Upgrade to track readiness trends and training balance.",
    upgradeButtonLabel: "Upgrade to Plus",
  },
  eventSessionLink: {
    requiredTier: "plus",
    title: "Plus Feature",
    description:
      "Linking events to sessions is available on the Plus plan. Upgrade to connect races and performances to your training.",
    upgradeButtonLabel: "Upgrade to Plus",
  },
  activityInsights: {
    requiredTier: "plus",
    title: "Plus Feature",
    description:
      "Activity Analytics are available on the Plus plan. Upgrade to view detailed activity breakdowns and exercise analytics.",
    upgradeButtonLabel: "Upgrade to Plus",
  },
  exportCsv: {
    requiredTier: "plus",
    title: "Plus Feature",
    description:
      "Exporting your data to CSV is available on the Plus plan. Upgrade to download and analyse your training data.",
    upgradeButtonLabel: "Upgrade to Plus",
  },
  personalBestPredictor: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "The Personal Best Predictor is available on the Premium plan. Upgrade to see your projected PB based on your training trends.",
    upgradeButtonLabel: "Upgrade to Premium",
  },

  // ─── GRAPHS, TRENDS & ANALYTICS ─────────────────────────
  advancedGraphs: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "Review Monthly, Quarterly & Custom Date Range graphs to gain deeper performance insights, personalised trends, and smarter guidance from your data.",
    upgradeButtonLabel: "Upgrade to Premium",
  },

  // ─── WEARABLE DEVICES ───────────────────────────────────
  heartLungRecovery: {
    requiredTier: "plus",
    title: "Heart Lung Recovery Index",
    description:
      "Heart Lung Recovery Index is available on the Plus or Premium plan. Upgrade to monitor your cardiovascular recovery trends.",
    upgradeButtonLabel: "Upgrade to Plus",
  },
  additionalWearables: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "Free includes 1 wearable, Plus includes 2, and Premium includes up to 3. Upgrade to connect more devices.",
    upgradeButtonLabel: "Upgrade Plan",
  },

  // ─── AI ─────────────────────────────────────────────────
  wizardAi: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "Get individualised insights and analysis on your data. Chat with the Wizard AI to dive deeper into your data. Adjust the tone and types of insights you want.",
    upgradeButtonLabel: "Upgrade to Premium",
  },
  aiInsights: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "Individualised AI Insights are available on the Premium plan. Upgrade to get personalised analysis of your training data.",
    upgradeButtonLabel: "Upgrade to Premium",
  },
  customInsightsTone: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "Custom Insights & Wizard Tone settings are available on the Premium plan. Upgrade to personalise your AI experience.",
    upgradeButtonLabel: "Upgrade to Premium",
  },
  olyAiHelper: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "Oly the AI Helper is available on the Premium plan. Upgrade to chat with your personal AI training assistant.",
    upgradeButtonLabel: "Upgrade to Premium",
  },
  aiChatLimit: {
    requiredTier: "premium",
    title: "Daily Chat Limit Reached",
    description:
      "Free and Plus users can start one chat every 24 hours. Upgrade to Premium for unlimited chats.",
    upgradeButtonLabel: "Upgrade to Premium",
  },

  // ─── SESSION / TRAINING PLANNER ─────────────────────────
  savedSessions: {
    requiredTier: "plus",
    title: "Plus Feature",
    description:
      "Saved Sessions & Exercises are available on the Plus plan. Upgrade to save and reuse your favourite training sessions.",
    upgradeButtonLabel: "Upgrade to Plus",
  },
  sessionBuilder: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "Advanced Session Builder is available on the Premium plan. Upgrade to create custom session templates and training plans.",
    upgradeButtonLabel: "Upgrade to Premium",
  },

  // ─── GOALS (AI-linked) ─────────────────────────────────
  performanceGoals: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "Set your performance goals to help the PEAK Wizard AI guide your personalised insights and help you make better training decisions.",
    upgradeButtonLabel: "Upgrade to Premium",
  },
  healthGoals: {
    requiredTier: "premium",
    title: "Premium Feature",
    description:
      "Set your health goals to help the PEAK Wizard AI guide your personalised insights and help you make better training decisions.",
    upgradeButtonLabel: "Upgrade to Premium",
  },

  // ─── GOALS (Training Diary) ─────────────────────────────
  performanceGoalSetting: {
    requiredTier: "plus",
    title: "Plus Feature",
    description:
      "Performance goal setting is available on the Plus plan. Upgrade to set and track your goals.",
    upgradeButtonLabel: "Upgrade to Plus",
  },

  // ─── CUSTOMISATION ──────────────────────────────────────
  dashboardBackground: {
    requiredTier: "plus",
    title: "Plus Feature",
    description:
      "Custom Dashboard Backgrounds are available on the Plus or Premium plan. Upgrade to personalise your dashboard backgrounds.",
    upgradeButtonLabel: "Upgrade to Plus",
  },
} as const;

/**
 * Look up a feature gate. Returns undefined for unknown keys.
 */
export function getFeatureGate(featureKey: string): FeatureGate | undefined {
  return FEATURE_GATES[featureKey];
}
